/*
 * /auth/account (page)
 */
import { supabase } from "@psychology/auth/client"
import { appConfig } from "@psychology/config"
import { LanguageSelect } from "@psychology/intl"
import { PushPreferenceButton } from "@psychology/push-notifications"
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
	Divider,
	Input,
	Label,
	Page,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	toast,
} from "@psychology/react-ui"
import { ThemeSelect } from "@psychology/theme"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { trpc } from "../../trpc"
import { authPageLoaderAuthenticated } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/account")({
	component: AccountPage,
	loader: authPageLoaderAuthenticated(),
})

function AccountPage() {
	const {
		user: { id, email },
	} = Route.useLoaderData()
	const { t } = useTranslation()
	const navigate = useNavigate()

	const { data: user, isLoading } = trpc.user.getByFilter.useQuery({
		where: {},
	})

	const utils = trpc.useUtils()
	const updateUser = trpc.user.update.useMutation({
		onSuccess: () => {
			utils.user.getByFilter.invalidate()
			toast.success(t("auth.updateSuccess"))
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	const [preferredName, setPreferredName] = useState("")
	const [avatarUrl, setAvatarUrl] = useState("")

	useEffect(() => {
		if (user) {
			setPreferredName(user.preferredName ?? "")
			setAvatarUrl(user.avatarUrl ?? "")
		}
	}, [user])

	function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
		e.preventDefault()
		updateUser.mutate({ id, preferredName, avatarUrl: avatarUrl || null })
	}

	async function handleSignOut() {
		await supabase.auth.signOut()
		navigate({ to: appConfig.redirects.afterSignOutUrl })
	}

	async function handleLocaleChange(locale: string) {
		await supabase.auth.updateUser({ data: { locale } })
	}

	const saving = updateUser.isPending

	return (
		<Page centerVertical={false} className={"bg-primary/30 pt-16"}>
			<Card className={"w-full max-w-md p-8 pb-12"}>
				<CardHeader className={"items-center gap-2 pb-4"}>
					<CardTitle>{t("auth.account")}</CardTitle>
					<CardDescription>{email}</CardDescription>
				</CardHeader>
				<CardContent>
					<Tabs defaultValue={"profile"}>
						<TabsList className={"w-full mb-6"}>
							<TabsTrigger value={"profile"} className={"flex-1"}>
								{t("common.profile")}
							</TabsTrigger>
							<TabsTrigger value={"preferences"} className={"flex-1"}>
								{t("common.preferences")}
							</TabsTrigger>
						</TabsList>

						<div>
							<TabsContent value={"profile"}>
								<form onSubmit={handleSubmit} className={"flex flex-col gap-6"}>
									<div className={"grid gap-2"}>
										<Label htmlFor={"preferred-name"}>{t("auth.name")}</Label>
										<Input
											id={"preferred-name"}
											type={"text"}
											placeholder={"John Doe"}
											value={preferredName}
											onChange={(e) => setPreferredName(e.target.value)}
											disabled={isLoading}
											required
										/>
									</div>
									<div className={"grid gap-2"}>
										<Label htmlFor={"avatar-url"}>{t("auth.avatarUrl")}</Label>
										<Input
											id={"avatar-url"}
											type={"url"}
											placeholder={"https://example.com/avatar.png"}
											value={avatarUrl}
											onChange={(e) => setAvatarUrl(e.target.value)}
											disabled={isLoading}
										/>
									</div>
									<Button
										type={"submit"}
										className={"w-full"}
										disabled={isLoading || saving}
									>
										{saving ? t("auth.saving") : t("auth.update")}
									</Button>
									<Divider />
									<Button
										type={"button"}
										variant={"outline"}
										className={"w-full"}
										onClick={handleSignOut}
									>
										{t("auth.signOut")}
									</Button>
								</form>
							</TabsContent>

							<TabsContent value={"preferences"}>
								<div className={"flex flex-col gap-5"}>
									<div className={"flex items-center justify-between gap-4"}>
										<Label>{t("common.language")}</Label>
										<LanguageSelect onAfterChange={handleLocaleChange} />
									</div>
									<div className={"flex items-center justify-between gap-4"}>
										<Label>{t("theme.label")}</Label>
										<ThemeSelect
											labels={{
												light: t("theme.light"),
												dark: t("theme.dark"),
												system: t("theme.system"),
											}}
										/>
									</div>
									<div className={"flex items-center justify-between gap-4"}>
										<Label>{t("pushNotifications.label")}</Label>
										<PushPreferenceButton
											userId={id}
											labels={{
												enable: t("common.enable"),
												disable: t("common.disable"),
												loading: t("common.loading"),
												notSupported: t("pushNotifications.notSupported"),
											}}
										/>
									</div>
								</div>
							</TabsContent>
						</div>
					</Tabs>
				</CardContent>
			</Card>
		</Page>
	)
}
